/**
 * Classifies stayuptodate_components rows for template Shop vs Content tabs.
 * Kept in sync with link-in-bio grouping expectations.
 */

import { isProductCollectionType } from "../productCollection";

const SHOPPABLE_TYPES = [
  "links",
  "query-links",
  "trending products",
  "trending content",
  "products-carousel",
  "Yesterday",
  "This Week",
  "This Month",
  "daywise",
];

/** Website template auto-series rows in `template_components` (backend-generated). */
export const CONTENT_SERIES_COMPONENT_TYPE = "content-series";

const CONTENT_TYPES = [
  "content",
  "query-content",
  "reels",
  "featured",
  "podcast",
  "latest-podcast",
  "all",
];

const SERIES_TYPES = [CONTENT_SERIES_COMPONENT_TYPE];

const CONTENT_DISPLAY_NAMES = [
  "tiktok",
  "instagram story",
  "podcast",
  "latest-podcast",
  "twitter",
  "linkedin",
  "youtube",
  "this week content",
  "reels",
  "all",
];

/** Types that should never appear in the Content tab (e.g. highlight with youtube display_name). */
const CONTENT_EXCLUDED_TYPES = [
  "highlight",
  "banner",
  "link-section",
  "collection-carousel",
];

function isDayWindowTypeOrDisplay(type?: string, displayName?: string): boolean {
  const typeLc = String(type || "")
    .trim()
    .toLowerCase();
  const displayLc = String(displayName || "")
    .trim()
    .toLowerCase();
  return (
    displayLc === "yesterday" ||
    displayLc === "this week" ||
    displayLc === "this month" ||
    displayLc === "this week content" ||
    typeLc === "yesterday" ||
    typeLc === "this week" ||
    typeLc === "this month" ||
    typeLc === "daywise"
  );
}

function isShoppable(component: {
  type?: string;
  display_name?: string;
  shoppable_content_filter?: string | null;
  shoppableContentFilter?: string | null;
}): boolean {
  if (!component?.type && !component?.display_name) return false;
  const type = (component.type ?? "").toString().trim();
  const displayName = (component.display_name ?? "").toString().trim();
  const typeLc = type.toLowerCase();
  const displayLc = displayName.toLowerCase();
  // Yesterday / This Week / This Month are shoppable day-windows even when
  // shoppable_content_filter is "content". That filter only chooses platforms
  // inside get_yesterday_links (see useLoadYesterdayLinks), it does not change
  // the carousel type. "This Week Content" is the content-carousel display name.
  if (type && SHOPPABLE_TYPES.some((t) => t.toLowerCase() === typeLc)) return true;
  if (displayName && SHOPPABLE_TYPES.some((t) => t.toLowerCase() === displayLc)) return true;
  return false;
}

function isContent(component: { type?: string; display_name?: string }): boolean {
  if (!component?.type && !component?.display_name) return false;
  const type = (component.type ?? "").toString().trim();
  const displayName = (component.display_name ?? "").toString().toLowerCase().trim();
  const typeLc = type.toLowerCase();
  if (type && CONTENT_EXCLUDED_TYPES.some((e) => e.toLowerCase() === typeLc)) return false;
  if (type && CONTENT_TYPES.some((ct) => ct.toLowerCase() === typeLc)) return true;
  if (CONTENT_DISPLAY_NAMES.some((d) => d === displayName || d === typeLc)) return true;
  return false;
}

/**
 * Classify a template `CarouselData` row (same precedence as useCreatorDataFromDB:
 * shoppable wins when both could apply).
 */
export function isCarouselShopTab(c: {
  componentType?: string;
  componentDisplayName?: string;
  shoppable_content_filter?: string;
  shoppableContentFilter?: string;
}): boolean {
  return isShoppable({
    type: c.componentType,
    display_name: c.componentDisplayName,
    shoppable_content_filter:
      c.shoppable_content_filter ?? c.shoppableContentFilter,
  });
}

function isSeries(component: { type?: string }): boolean {
  const type = (component.type ?? "").toString().trim().toLowerCase();
  return SERIES_TYPES.some((t) => t.toLowerCase() === type);
}

export function isCarouselSeriesTab(c: {
  componentType?: string;
}): boolean {
  return isSeries({ type: c.componentType });
}

export function isCarouselContentTab(c: {
  componentType?: string;
  componentDisplayName?: string;
  shoppable_content_filter?: string;
  shoppableContentFilter?: string;
}): boolean {
  if (isCarouselShopTab(c)) return false;
  if (isCarouselSeriesTab(c)) return false;
  return isContent({
    type: c.componentType,
    display_name: c.componentDisplayName,
  });
}

/**
 * Allowed component types for the "Latest" tab on the Recent Content page.
 * Only platform/automated-feed carousels appear here.
 */
const LATEST_TAB_TYPES = new Set([
  "reels",
  "instagram story",
  "tiktok",
  "podcast",
  "latest-podcast",
  "twitter",
  "linkedin",
  "youtube",
  "featured",
  "all",
]);

/**
 * Day-wise / trending carousels (same as Link in Bio shop-links automations).
 * On the website Master Template they belong on Recent Content → Latest, not Shop.
 */
const AUTOMATED_RECENT_CONTENT_TYPES = new Set([
  "daywise",
  "trending products",
  "trending content",
]);

const AUTOMATED_RECENT_CONTENT_DISPLAY_NAMES = new Set([
  "yesterday",
  "this week",
  "this month",
  "this week content",
]);

/** Relative day-window rows (Yesterday / This Week / This Month / This Week Content). */
export function isRelativeDayWindowDisplay(
  displayName?: string | null,
  type?: string | null,
): boolean {
  return isDayWindowTypeOrDisplay(type ?? undefined, displayName ?? undefined);
}

/**
 * Day-window whose Show scope is content (e.g. bellabranchdiy "Yesterday's Links").
 * These share Yesterday/This Week display_names with Product Feeds but must not
 * open the products editor or badge as Product Feed.
 */
export function isContentScopedDayWindow(c: {
  shoppable_content_filter?: string | null;
  display_name?: string | null;
  type?: string | null;
  componentDisplayName?: string | null;
  componentType?: string | null;
}): boolean {
  const filterLc = String(c.shoppable_content_filter ?? "")
    .trim()
    .toLowerCase();
  if (filterLc !== "content") return false;
  return isRelativeDayWindowDisplay(
    c.display_name ?? c.componentDisplayName,
    c.type ?? c.componentType,
  );
}

/**
 * Product-oriented collection titles that should open on the Products page
 * (and stay typed as `shop`), even when day-window heuristics would otherwise
 * push them onto Recent Content.
 *
 * Content-feed Add (type reels/all/tiktok/…, Show ≠ products) must NOT match
 * on title alone — e.g. "Shop this past week" is a valid content strip title.
 */
export function isWebsiteProductCollectionCarousel(c: {
  name?: string;
  title?: string;
  type?: string;
  componentType?: string;
  details?: string;
  shoppable_content_filter?: string;
}): boolean {
  const typeLc = String(c.type ?? c.componentType ?? "")
    .trim()
    .toLowerCase();
  // Content Keywords are never product collections — even with a stale
  // shoppable_content_filter=products left by older edit/save bugs.
  if (typeLc === "query-content" || typeLc === "content-series") {
    return false;
  }

  const filterLc = String(c.shoppable_content_filter ?? "")
    .trim()
    .toLowerCase();
  if (filterLc === "products") return true;
  // Explicit content Show scope wins over title keywords like "Shop this week".
  if (filterLc === "content") return false;

  const detailsRaw = String(c.details ?? "").trim();
  if (detailsRaw.startsWith("{")) {
    try {
      const parsed = JSON.parse(detailsRaw) as { feedKind?: string };
      if (parsed?.feedKind === "product-feed") return true;
    } catch {
      /* ignore */
    }
  }

  // Platform content feeds are never product collections by title keywords.
  if (typeLc && LATEST_TAB_TYPES.has(typeLc)) return false;

  const nameLc = String(c.name ?? c.title ?? "")
    .trim()
    .toLowerCase();
  return (
    nameLc.includes("trending this month") ||
    nameLc.includes("shop this past week") ||
    nameLc.includes("shop this week") ||
    (nameLc.includes("shop") &&
      (nameLc.includes("past week") || nameLc.includes("this week")))
  );
}

/**
 * True for automated day-wise / trending rows that should appear under
 * Recent Content (Latest) in the Master Template — even though LiB classifies
 * some of the same type strings as shoppable.
 */
export function isAutomatedRecentContentCarousel(c: {
  type?: string;
  display_name?: string;
  name?: string;
  title?: string;
  componentType?: string;
  componentDisplayName?: string;
  shoppable_content_filter?: string;
}): boolean {
  // LiB dual-written "… Shoppable" / products-only forks belong on Shop (or nowhere
  // on the website Latest strip) — not Recent Content manage/tabs.
  const nameLc = String(c.name ?? c.title ?? "")
    .trim()
    .toLowerCase();
  if (nameLc.includes("shoppable")) return false;
  const filterLc = String(c.shoppable_content_filter ?? "")
    .trim()
    .toLowerCase();
  // Explicit content day-windows created from the website editor.
  const typeLc = String(c.type ?? c.componentType ?? "")
    .trim()
    .toLowerCase();
  const displayLc = String(c.display_name ?? c.componentDisplayName ?? "")
    .trim()
    .toLowerCase();

  if (isWebsiteProductCollectionCarousel(c)) return false;

  const isContentDayWindow =
    displayLc === "yesterday" ||
    displayLc === "this week" ||
    displayLc === "this month" ||
    displayLc === "this week content" ||
    nameLc.includes("yesterday") ||
    nameLc.includes("this week") ||
    nameLc.includes("this month");
  if (filterLc === "products" && !isContentDayWindow) return false;

  if (typeLc && AUTOMATED_RECENT_CONTENT_TYPES.has(typeLc)) return true;
  if (displayLc && AUTOMATED_RECENT_CONTENT_DISPLAY_NAMES.has(displayLc)) {
    return true;
  }
  return false;
}

/**
 * Returns true when a CarouselData row should appear under the "Latest" tab
 * of the Recent Content page. Only the specific platform/feed carousel types,
 * automated day-wise / trending carousels, and "This Week Content" qualify.
 */
export function isCarouselLatestTab(c: {
  componentType?: string;
  componentDisplayName?: string;
  name?: string;
  title?: string;
  shoppable_content_filter?: string;
  shoppableContentFilter?: string;
}): boolean {
  if (isCarouselSeriesTab(c)) return false;
  if (
    isAutomatedRecentContentCarousel({
      componentType: c.componentType,
      componentDisplayName: c.componentDisplayName,
      name: c.name,
      title: c.title,
      shoppable_content_filter:
        c.shoppable_content_filter ?? c.shoppableContentFilter,
    })
  ) {
    return true;
  }
  if (isCarouselShopTab(c)) return false;
  const typeLc = (c.componentType ?? "").toString().trim().toLowerCase();
  const displayLc = (c.componentDisplayName ?? "").toString().trim().toLowerCase();
  if (typeLc && LATEST_TAB_TYPES.has(typeLc)) return true;
  // "This Week Content" is stored with type="" and display_name="This Week Content"
  if (displayLc === "this week content") return true;
  return false;
}

export { isShoppable, isContent };

/** LiB carousel title → website Products vs Content. Product-bank collections keep their own URLs. */
export function websiteCatalogPageForLibComponent(
  component: any,
): "shop" | "content" | null {
  if (!component || isProductCollectionType(component)) return null;

  const type = component.type;
  const display_name = component.display_name;
  const filter = String(component.shoppable_content_filter || "")
    .trim()
    .toLowerCase();
  const typeLc = String(type || "").toLowerCase().trim();

  // Old LiB Collections store member *component* ids in post_ids — Shop expands
  // them; Content treats them as platform posts and renders empty.
  if (
    typeLc === "carousel-collection" ||
    typeLc === "collection-carousel"
  ) {
    return "shop";
  }

  // Same product-named collections the website Shop page already treats as shop
  // (e.g. "Shop This Past Week"), plus explicit product feeds.
  if (
    isWebsiteProductCollectionCarousel({
      name: component.name,
      title: display_name,
      type,
      details: component.details,
      shoppable_content_filter: component.shoppable_content_filter,
    })
  ) {
    return "shop";
  }
  // Website Custom Products (`links`) always open on Shop as an individual page.
  if (typeLc === "links" || typeLc === "products-carousel") {
    return "shop";
  }

  // Content Keywords stay on content/series even with a stale products filter.
  if (typeLc === "query-content" || typeLc === "content-series") {
    return "content";
  }

  if (filter === "products") return "shop";
  // display_name "This Week Content" is the only content day-window type.
  // Yesterday / This Week / This Month are shoppable links carousels;
  // shoppable_content_filter only filters platforms in that feed.
  if (String(display_name || "").trim().toLowerCase() === "this week content") {
    return "content";
  }
  // Shoppable types win over content (same as isCarouselContentTab).
  if (isShoppable({ type, display_name })) return "shop";
  if (isCarouselSeriesTab({ componentType: type })) return "content";
  if (filter === "content") return "content";
  if (isContent({ type, display_name })) return "content";
  return null;
}
